import React from "react";
import axios from "axios";
import _ from "lodash";
import { GetServerSideProps } from "next/types";
import { ApolloClient, InMemoryCache } from "@apollo/client";

import PostPage from "@/components/PageComponents/PostPage";
import ContentPage from "@/components/PageComponents/ContentPage";
import { handleError } from "@/helpers/handleError";

import {
  GET_POST,
  GET_LATEST_CATEGORY_POSTS,
  GET_POST_BY_ID,
  GET_SAFE_FIRST_POST,
  GET_SAFE_SECOND_POST,
  GET_TOPICS_DATA,
  GET_CONTENT_PAGE,
  GET_HEADER_DATA,
  GET_FOOTER_DATA,
  GET_AUTHOR_ARTICLES_TOTAL,
} from "@/graphql/queries";
import { BreadcrumbType } from "@/Types/types";
import { breadcrumbInitial } from "@/data/breadcrumbInitial";

export default function Slug(props: any) {
  return props?.type === "post" ? (
    <PostPage {...props} />
  ) : props?.type === "page" ? (
    <ContentPage {...props} />
  ) : null;
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    context.res.setHeader(
      "Cache-Control",
      "public, s-maxage=10, stale-while-revalidate=59"
    );

    const client = new ApolloClient({
      uri: `${process.env.NEXT_PUBLIC_API_URL}/graphql`,
      cache: new InMemoryCache(),
    });

    const { data: headerData } = await client.query({
      query: GET_HEADER_DATA,
    });

    const { data: footerData } = await client.query({
      query: GET_FOOTER_DATA,
    });

    const { data: postData } = await client.query({
      query: GET_POST,
      variables: { slugUrl: context.params?.slug },
    });

    const breadcrumbs: BreadcrumbType[] = JSON.parse(JSON.stringify(breadcrumbInitial));

    breadcrumbs.push({
      href: `/blog/categoria/${postData.post.data[0].attributes.category.data[0].attributes.slug}`,
      label: `${postData.post.data[0].attributes.category.data[0].attributes.title}`,
    });
    breadcrumbs.push({
      href: `/blog/${postData.post.data[0].attributes.url}`,
      label: `${postData.post.data[0].attributes.title}`,
    });

    if (!postData.post.data.length) {
      const { data: pageData } = await client.query({
        query: GET_CONTENT_PAGE,
        variables: { slugUrl: context.params?.slug },
      });

      if (pageData.page.data.length) {
        return {
          props: {
            ...headerData,
            ...footerData,
            ...pageData,
            type: "page",
          },
        };
      }
    }

    const { data: categoryPosts } = await client.query({
      query: GET_LATEST_CATEGORY_POSTS,
      variables: {
        category:
          postData.post.data[0].attributes.category.data[0].attributes.title,
        id: postData.post.data[0].id,
      },
    });

    const prevId =
      +postData.post.data[0].id === 1
        ? +postData.post.data[0].id + 2
        : +postData.post.data[0].id - 1;

    const {
      data: { articles: prevArticle },
    } = await client.query({
      query: GET_POST_BY_ID,
      variables: { id: prevId },
    });

    const {
      data: { articles: nextArticle },
    } = await client.query({
      query: GET_POST_BY_ID,
      variables: { id: +postData.post.data[0].id + 1 },
    });

    const {
      data: { articles: safeArticleOne },
    } = await client.query({
      query: GET_SAFE_FIRST_POST,
    });

    const {
      data: { articles: safeArticleTwo },
    } = await client.query({
      query: GET_SAFE_SECOND_POST,
    });

    const comments = await axios.get(
      `${process.env.NEXT_PUBLIC_API_URL}/api/comments/api::article.article:${postData.post.data[0].id}?filters[approvalStatus][$eq]=APPROVED&filters[blocked][$ne]=true`
    );

    const { data: topicsData } = await client.query({
      query: GET_TOPICS_DATA,
    });

    const {
      data: {
        articles: {
          meta: {
            pagination: { total: authorArticlesTotal },
          },
        },
      },
    } = await client.query({
      query: GET_AUTHOR_ARTICLES_TOTAL,
      variables: { authorId: postData.post.data[0].attributes.author.data.id },
    });

    return {
      props: {
        ...headerData,
        ...footerData,
        ...postData,
        ...topicsData,
        ...categoryPosts,
        authorArticlesTotal,
        breadcrumbs,
        prevArticle,
        nextArticle,
        safeArticleOne,
        safeArticleTwo,
        comments: comments.data,
        type: "post",
      },
    };
  } catch (error) {
    handleError(error, "Slug getServerSideProps");
    return {
      notFound: true,
    };
  }
};
